import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileCheck, Briefcase, Globe, ClipboardList, Shield, Users, BookOpen, Receipt, Calculator } from 'lucide-react';
import { db, doc, getDoc } from '@/firebase';
import { Link } from 'react-router-dom';

const services = [
  {
    title: "Pembuatan SBU",
    desc: "Sertifikat Badan Usaha untuk legalitas perusahaan konstruksi Anda.",
    icon: Shield,
    slug: "sbu"
  },
  {
    title: "Sertifikat SKK",
    desc: "Sertifikat Kompetensi Kerja bagi tenaga ahli konstruksi.",
    icon: FileCheck,
    slug: "skk"
  },
  {
    title: "NIB & OSS",
    desc: "Pengurusan Nomor Induk Berusaha dan perizinan sistem OSS RBA.",
    icon: Globe,
    slug: "nib-oss"
  },
  {
    title: "Konsultasi Perizinan",
    desc: "Pendampingan lengkap untuk perizinan proyek dan usaha.",
    icon: Briefcase,
    slug: "konsultasi-perizinan"
  },
  {
    title: "Pendampingan Proyek",
    desc: "Bantuan administrasi dan teknis selama jalannya proyek.",
    icon: ClipboardList,
    slug: "pendampingan-proyek"
  },
  {
    title: "Penyusunan Dokumen Penawaran",
    desc: "Jasa penyusunan dokumen penawaran teknis dan administrasi tender.",
    icon: Users,
    slug: "penyusunan-dokumen-penawaran"
  },
  {
    title: "Pajak Badan & Orang Pribadi",
    desc: "Layanan Administrasi Profesional Pelaporan Pajak Badan Usaha dan Pajak Orang Pribadi secara terpadu.",
    icon: Receipt,
    slug: "pajak-badan-pribadi"
  },
  {
    title: "SPT Masa & E-Faktur",
    desc: "Layanan pelaporan SPT Masa PPN, SPT Masa PPh, serta pembuatan dan pengelolaan administrasi E-Faktur.",
    icon: Calculator,
    slug: "spt-masa-efaktur"
  },
  {
    title: "Edukasi & Pelatihan",
    desc: "Materi belajar terstruktur mengenai standar teknis dan regulasi konstruksi.",
    icon: BookOpen,
    slug: "edukasi-pelatihan"
  }
];

export default function Services({ limit, hideBanner }: { limit?: number, hideBanner?: boolean }) {
  const [promo, setPromo] = useState<any>(null);

  useEffect(() => {
    if (!hideBanner) {
      const fetchPromo = async () => {
        try {
          const promoSnap = await getDoc(doc(db, 'settings', 'promo_services'));
          if (promoSnap.exists()) {
            setPromo(promoSnap.data());
          } else {
            setPromo({
              isActive: true,
              title: 'Layanan Administrasi Profesional',
              subtitle: 'Solusi legalitas dan pendampingan bisnis tercepat dan terpercaya.',
              bannerUrl: 'https://images.unsplash.com/photo-1454165833767-027ffea10c3b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
              badgeText: 'Promo Layanan'
            });
          }
        } catch (error) {
          console.error('Error fetching promo:', error);
        }
      };
      fetchPromo();
    }
  }, [hideBanner]);

  const displayedServices = limit ? services.slice(0, limit) : services;

  return (
    <section className="py-12 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-0">
        
        {/* Promotional Banner */}
        {!hideBanner && promo?.isActive && (
          <div className="mb-10 md:mb-16 bg-card sm:rounded-2xl md:rounded-3xl overflow-hidden relative shadow-xl -mx-4 sm:mx-0 border border-border">
            {promo.bannerUrl && (
              <img 
                src={promo.bannerUrl} 
                alt="Promo Banner" 
                className="w-full h-auto block" 
                referrerPolicy="no-referrer" 
              />
            )}
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 relative z-30">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-5xl font-bold mb-4"
            >
              Layanan <span className="text-primary">Administrasi</span> {limit ? 'Unggulan' : 'Profesional'}
            </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-foreground/60 max-w-2xl mx-auto"
          >
            Kami membantu mempermudah urusan birokrasi dan administrasi usaha konstruksi Anda 
            agar Anda bisa fokus pada pembangunan.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-30">
          {displayedServices.map((service, i) => {
            const isEducation = service.title === "Edukasi & Pelatihan";
            
            const content = (
              <Card className="bg-card border-border hover:border-gold/50 transition-all group cursor-pointer h-full">
                <CardHeader>
                  <div className="h-16 w-16 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mb-5 group-hover:from-gold group-hover:to-gold-light group-hover:scale-110 transition-all duration-300 shadow-sm border border-gold/10">
                    <service.icon strokeWidth={1.5} className="h-8 w-8 text-primary group-hover:text-navy transition-colors duration-300" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/60 leading-relaxed">
                    {service.desc}
                  </p>
                </CardContent>
              </Card>
            );
            
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={isEducation ? "/education" : `/services/${service.slug}`} className="block h-full">
                  {content}
                </Link>
              </motion.div>
            );
          })}
        </div>
        
        {limit && displayedServices.length < services.length && (
          <div className="mt-12 text-center relative z-30">
            <Button asChild className="bg-transparent border-2 border-primary/20 text-foreground hover:bg-primary hover:text-primary-foreground font-bold h-12 px-8 rounded-xl shadow-none">
              <Link to="/services">Lihat Semua Layanan</Link>
            </Button>
          </div>
        )}

      </div>
    </div>
  </section>
);
}
